"use client"

import { css, cx } from "@emotion/css"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { CellAttributes, Cells, TableAttributes } from "@/../types/GutenbergBlockAttributes"
import ParsedText from "@/components/course-material/ParsedText"
import { baseTheme } from "@/shared-module/common/styles"
import { stringToNumberOrPlaceholder } from "@/shared-module/common/utils/numbers"
import withErrorBoundary from "@/shared-module/common/utils/withErrorBoundary"

import type { BlockRendererProps } from "../.."

interface ExtraAttributes {
  className?: string
}

const scrollContainerStyles = css`
  overflow-x: auto;
  overflow-y: hidden;
`

const scrollWrapperStyles = css`
  position: relative;
`

// Edge fades that hint the table scrolls horizontally. Shown only on the side(s) with hidden content.
const edgeShadowBaseStyles = css`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2rem;
  pointer-events: none;
`

const rightShadowStyles = css`
  right: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.16));
`

const leftShadowStyles = css`
  left: 0;
  background: linear-gradient(to left, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.16));
`

const fetchAlignment = (align: string | undefined) => {
  if (align) {
    return css`
      text-align: ${align};
    `
  }
  return undefined
}

const TableBlock: React.FC<
  React.PropsWithChildren<BlockRendererProps<TableAttributes & ExtraAttributes>>
> = ({ data, dontAllowBlockToBeWiderThanContainerWidth }) => {
  const {
    hasFixedLayout,
    caption,
    className,
    // borderColor, // Bordercolor is same as textcolor in our version of Gutenberg
    // style,
  } = data.attributes
  const body = data.attributes.body
  const head = data.attributes.head
  const foot = data.attributes.foot

  const refsMap = useRef(new Map<string, React.RefObject<HTMLElement | null>>()).current

  const getRef = (key: string) => {
    if (!refsMap.has(key)) {
      refsMap.set(key, React.createRef<HTMLElement>())
    }
    // oxlint-disable-next-line typescript/no-non-null-assertion -- key was just inserted above, so get(key) is defined
    return refsMap.get(key)!
  }

  const hasManyColumns = useMemo(() => {
    for (const row of body) {
      if (row.cells && row.cells.length > 5) {
        return true
      }
    }

    return false
  }, [body])
  const shouldUseSmallerFont = hasManyColumns && dontAllowBlockToBeWiderThanContainerWidth

  const isStriped = className === "is-style-stripes"

  const captionRef = useRef<HTMLElement>(null)

  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScroll, setCanScroll] = useState({ left: false, right: false })

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    const left = el.scrollLeft > 1
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    setCanScroll((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    updateScrollState()
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)
    return () => resizeObserver.disconnect()
  }, [updateScrollState])

  return (
    <div className={scrollWrapperStyles}>
      <div ref={scrollRef} onScroll={updateScrollState} className={scrollContainerStyles}>
        <table
          className={css`
            border-collapse: collapse;
            td,
            th {
              ${!isStriped && `border: 1px solid currentColor;`}
              white-space: pre-wrap;
              padding: 0.5rem;
              ${hasFixedLayout && "overflow-wrap: break-word;"}
            }
            /* stylelint-disable-next-line block-no-empty */
            tbody tr:nth-child(odd) {
              ${isStriped && `background-color: ${baseTheme.colors.gray[100]};`}
            }
            ${hasFixedLayout && "table-layout: fixed;"}
            thead {
              border-bottom: 3px solid;
            }
            tfoot {
              border-top: 3px solid;
            }

            ${shouldUseSmallerFont && `font-size: 15px;`}
          `}
        >
          {head && (
            <thead>
              {head.map((cellRows: Cells, j: number) => (
                <tr key={j}>
                  {cellRows.cells &&
                    cellRows.cells.map((cell: CellAttributes, i) => (
                      <ParsedText
                        key={i}
                        // oxlint-disable-next-line i18next/no-literal-string
                        text={cell.content !== "" ? (cell.content ?? "&#xFEFF;") : "&#xFEFF;"}
                        tag="th"
                        tagProps={{
                          // oxlint-disable-next-line i18next/no-literal-string
                          scope: "col",
                          className: fetchAlignment(cell.align),
                          colSpan: stringToNumberOrPlaceholder(cell.colspan, undefined),
                          rowSpan: stringToNumberOrPlaceholder(cell.rowspan, undefined),
                        }}
                        useWrapperElement={false}
                        // oxlint-disable-next-line i18next/no-literal-string
                        wrapperRef={getRef(`head-${j}-${i}`)}
                      />
                    ))}
                </tr>
              ))}
            </thead>
          )}
          <tbody>
            {body.map((cellRows: Cells, j: number) => (
              <tr key={j}>
                {cellRows.cells &&
                  cellRows.cells.map((cell: CellAttributes, i: number) => (
                    <ParsedText
                      key={i}
                      // oxlint-disable-next-line i18next/no-literal-string
                      text={cell.content !== "" ? (cell.content ?? "&#xFEFF;") : "&#xFEFF;"}
                      tag={cell.tag === "th" ? "th" : "td"}
                      tagProps={{
                        ...(cell.tag === "th"
                          ? // oxlint-disable-next-line i18next/no-literal-string
                            { scope: cell.scope ?? "row" }
                          : {}),
                        className: fetchAlignment(cell.align),
                        colSpan: stringToNumberOrPlaceholder(cell.colspan, undefined),
                        rowSpan: stringToNumberOrPlaceholder(cell.rowspan, undefined),
                      }}
                      useWrapperElement={false}
                      // oxlint-disable-next-line i18next/no-literal-string
                      wrapperRef={getRef(`body-${j}-${i}`)}
                    />
                  ))}
              </tr>
            ))}
          </tbody>
          {foot && (
            <tfoot>
              {foot.map((cellRows: Cells, j: number) => (
                <tr key={j}>
                  {cellRows.cells &&
                    cellRows.cells.map((cell: CellAttributes, i: number) => (
                      <ParsedText
                        key={i}
                        // oxlint-disable-next-line i18next/no-literal-string
                        text={cell.content !== "" ? (cell.content ?? "&#xFEFF;") : "&#xFEFF;"}
                        tag="th"
                        tagProps={{
                          // oxlint-disable-next-line i18next/no-literal-string
                          scope: "col",
                          className: fetchAlignment(cell.align),
                          colSpan: stringToNumberOrPlaceholder(cell.colspan, undefined),
                          rowSpan: stringToNumberOrPlaceholder(cell.rowspan, undefined),
                        }}
                        useWrapperElement={false}
                        // oxlint-disable-next-line i18next/no-literal-string
                        wrapperRef={getRef(`foot-${j}-${i}`)}
                      />
                    ))}
                </tr>
              ))}
            </tfoot>
          )}
          <ParsedText
            text={caption}
            tag="caption"
            tagProps={{
              className: css`
                text-align: center;
                font-size: 0.8125rem;
                caption-side: bottom;
              `,
            }}
            useWrapperElement={false}
            wrapperRef={captionRef}
          />
        </table>
      </div>
      {canScroll.left && <div aria-hidden className={cx(edgeShadowBaseStyles, leftShadowStyles)} />}
      {canScroll.right && (
        <div aria-hidden className={cx(edgeShadowBaseStyles, rightShadowStyles)} />
      )}
    </div>
  )
}

export default withErrorBoundary(TableBlock)
